import { PixType } from '@portal/enum/pix';
import { translate } from '@portal/services/i18n';
import React, { useMemo, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import {
  isCpf,
  isCNPJ,
  maskCnpj,
  maskCpf,
  maskPhoneInput,
  unmaskField,
} from '@portal/services/masks';
import ThemedText from '../ThemedText/ThemedText';
import { isPhone } from '@portal/utils/phone';
import { AccountTaxIdentifierType } from '@portal/models/enumerators';
import useReduxState from '@portal/hooks/useReduxState';
import TokenIcon from '@portal/assets/svg/pix-buttons/new-key/ic_token.svg?react';
import { validateEmail } from '@portal/utils/validate';

interface CreatePixKeyProps {
  pixType: number;
  accountType?: number;
  newPixKey: string;
  setNewPixKey: (value: string) => void;
  onSubmit: () => void;
  onValidationChange?: (hasError: boolean) => void;
}

const createPixKeySchema = (pixType: number, accountType?: number) =>
  z.object({
    pixKey: z
      .string()
      .min(1, translate('PAGES.HOME.PIX.VALIDATION.PIX_KEY_REQUIRED'))
      .superRefine((val, ctx) => {
        if (pixType === PixType.EMAIL) {
          if (!validateEmail(val)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: translate(
                'PAGES.HOME.PIX.VALIDATION.PIX_KEY_INVALID_EMAIL'
              ),
            });
          }
          return;
        }
        if (pixType === PixType.PHONE) {
          const digits = unmaskField(val);
          if (digits.length < 10 || !isPhone(digits)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: translate(
                'PAGES.HOME.PIX.VALIDATION.PIX_KEY_INVALID_PHONE'
              ),
            });
          }
          return;
        }
        if (pixType !== PixType.TAX_IDENTIFIER) return;
        const digits = unmaskField(val);
        const isPf =
          accountType === undefined ||
          accountType === AccountTaxIdentifierType.PERSON;
        if (isPf) {
          if (digits.length !== 11 || !isCpf(digits)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: translate(
                'PAGES.HOME.PIX.VALIDATION.PIX_KEY_INVALID_CPF'
              ),
            });
          }
          return;
        }
        if (digits.length !== 14 || !isCNPJ(digits)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: translate(
              'PAGES.HOME.PIX.VALIDATION.PIX_KEY_INVALID_CNPJ'
            ),
          });
        }
      }),
  });

type CreatePixKeyForm = z.infer<ReturnType<typeof createPixKeySchema>>;

const CreatePixKey = ({
  newPixKey,
  onSubmit,
  pixType,
  accountType,
  setNewPixKey,
  onValidationChange,
}: CreatePixKeyProps) => {
  const {
    theme: { currentTheme },
  } = useReduxState();

  const schema = useMemo(
    () => createPixKeySchema(pixType, accountType),
    [pixType, accountType]
  );

  const {
    control,
    handleSubmit: handleFormSubmit,
    reset,
    formState: { errors },
  } = useForm<CreatePixKeyForm>({
    resolver: zodResolver(schema),
    defaultValues: { pixKey: newPixKey },
    mode: 'onChange',
  });

  useEffect(() => {
    reset({ pixKey: newPixKey });
  }, [pixType, accountType, reset]);

  const formHasError = !!errors.pixKey?.message;
  useEffect(() => {
    if (!onValidationChange) return;
    if (pixType === PixType.RANDOM) {
      onValidationChange(false);
      return;
    }
    onValidationChange(formHasError);
  }, [pixType, formHasError, onValidationChange]);

  const handleSubmitForm = (data: CreatePixKeyForm) => {
    setNewPixKey(data.pixKey);
    onSubmit();
  };

  const handleChangeByType = (value: string): string => {
    if (pixType === PixType.EMAIL) {
      setNewPixKey(value);
      return value;
    }
    if (pixType === PixType.PHONE) {
      const digits = unmaskField(value);
      if (digits.length > 11) return newPixKey;
      const next = digits.length > 0 ? maskPhoneInput(digits) : '';
      setNewPixKey(next);
      return next;
    }
    if (pixType === PixType.TAX_IDENTIFIER) {
      const digits = unmaskField(value);
      const isPf =
        accountType === undefined ||
        accountType === AccountTaxIdentifierType.PERSON;
      if (isPf) {
        if (digits.length > 11) return newPixKey;
        const next = digits.length > 0 ? maskCpf(digits) : '';
        setNewPixKey(next);
        return next;
      }
      if (digits.length > 14) return newPixKey;
      const next = digits.length > 0 ? maskCnpj(digits) : '';
      setNewPixKey(next);
      return next;
    }
    const digits = unmaskField(value);
    if (value.includes('@') || /[a-zA-Z]/.test(value)) {
      setNewPixKey(value);
      return value;
    }
    if (digits.length === 0) {
      setNewPixKey(value);
      return value;
    }
    if (digits.length === 11 && isCpf(digits)) {
      const next = maskCpf(digits);
      setNewPixKey(next);
      return next;
    }
    if (digits.length === 14 && isCNPJ(digits)) {
      const next = maskCnpj(digits);
      setNewPixKey(next);
      return next;
    }
    if (digits.length >= 12 && digits.length <= 14) {
      const next = maskCnpj(digits);
      setNewPixKey(next);
      return next;
    }
    if (isPhone(digits)) {
      const next = maskPhoneInput(digits);
      setNewPixKey(next);
      return next;
    }
    setNewPixKey(digits);
    return digits;
  };

  const getLabel = () => {
    if (pixType === PixType.EMAIL)
      return translate('PAGES.HOME.PIX.LABELS.EMAIL');
    if (pixType === PixType.PHONE)
      return translate('PAGES.HOME.PIX.LABELS.PHONE');
    if (pixType === PixType.TAX_IDENTIFIER)
      return translate('PAGES.HOME.PIX.LABELS.TAX_IDENTIFIER');
    return translate('PAGES.HOME.PIX.PIX_KEY_LABEL');
  };

  const getPlaceholder = () => {
    if (pixType === PixType.EMAIL)
      return translate('PAGES.HOME.PIX.PLACEHOLDER_EMAIL');
    if (pixType === PixType.PHONE)
      return translate('PAGES.HOME.PIX.PLACEHOLDER_PHONE');
    if (pixType === PixType.TAX_IDENTIFIER) {
      const isPf =
        accountType === undefined ||
        accountType === AccountTaxIdentifierType.PERSON;
      return isPf
        ? translate('PAGES.HOME.PIX.PLACEHOLDER_CPF')
        : translate('PAGES.HOME.PIX.PLACEHOLDER_CNPJ');
    }
    return translate('PAGES.HOME.PIX.PIX_KEY_PLACEHOLDER');
  };

  const getMaxLength = () => {
    if (pixType === PixType.EMAIL) return 77;
    if (pixType === PixType.PHONE) return 17;
    if (pixType === PixType.TAX_IDENTIFIER) {
      const isPf =
        accountType === undefined ||
        accountType === AccountTaxIdentifierType.PERSON;
      return isPf ? 14 : 18;
    }
    return 77;
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleFormSubmit(handleSubmitForm)(e);
  };

  if (pixType === PixType.RANDOM) {
    const iconColor =
      currentTheme?.primaryColor ?? currentTheme?.background ?? undefined;
    return (
      <div className="create-pix-key">
        <AdvancedCard testId="pix-key-card">
          <div className="create-pix-key__random">
            <div className="create-pix-key__random-icon">
              <TokenIcon fill={iconColor} width={40} height={40} />
            </div>
            <div className="create-pix-key__random-content">
              <ThemedText
                variant="body"
                className="create-pix-key__random-description"
              >
                {`${translate('PAGES.HOME.PIX.CREATE_PIX.RANDOM.FISRT_LINE')} `}
                <strong>
                  {translate('PAGES.HOME.PIX.CREATE_PIX.RANDOM.KEY')}
                </strong>
                {` ${translate(
                  'PAGES.HOME.PIX.CREATE_PIX.RANDOM.SECOND_LINE'
                )}`}
              </ThemedText>
            </div>
          </div>
        </AdvancedCard>
      </div>
    );
  }

  return (
    <div className="create-pix-key">
      <form onSubmit={handleSubmit} className="create-pix-key__form">
        <ThemedText variant="h2" className="create-pix-key__title">
          {translate(
            pixType === PixType.TAX_IDENTIFIER
              ? 'PAGES.HOME.PIX.CONFIRM_PIX_KEY_TITLE'
              : 'PAGES.HOME.PIX.INSERT_PIX_KEY_TITLE'
          )}
        </ThemedText>
        <Controller
          name="pixKey"
          control={control}
          render={({ field }) => (
            <AdvancedInput
              autoFocus
              name="pixKey"
              autoComplete="off"
              value={field.value}
              maxLength={getMaxLength()}
              onChange={(value) => {
                const next = handleChangeByType(value);
                field.onChange(next);
              }}
              label={getLabel()}
              placeholder={getPlaceholder()}
              error={!!errors.pixKey?.message}
              errorMessage={errors.pixKey?.message}
              disabled={pixType === PixType.TAX_IDENTIFIER}
            />
          )}
        />
      </form>
    </div>
  );
};

export default CreatePixKey;
